import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  inject,
  DestroyRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CaseService, Person } from '../../services/case-service/case.service';
import { DataTable, DataTableColumn, PageChangeEvent } from '../../shared-components/data-table/data-table';

@Component({
  selector: 'app-person-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, DataTable],
  templateUrl: './person-modal.html',
  styleUrl: './person-modal.scss',
})
export class PersonModal implements OnChanges, AfterViewInit, OnDestroy {
  protected readonly caseService = inject(CaseService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() action: any;
  @Input() caseId: string = '';
  @Input() preSelectedPeople: Person[] = [];
  @Output() closed = new EventEmitter<any>();

  @ViewChild('modalBox') modalBox!: ElementRef<HTMLDivElement>;
  @ViewChild('cancelBtn') cancelBtn!: ElementRef<HTMLButtonElement>;

  // Full Person objects, keyed by selection key — survives pages being unloaded
  selectedPeople = new Map<string, Person>();

  private initialized = false;
  private focusableElements: HTMLElement[] = [];
  private previouslyFocusedElement: HTMLElement | null = null;
  private keydownListener: ((e: KeyboardEvent) => void) | null = null;
  
  // FIX: Track pending timeouts to cancel them on destroy
  private pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

  columns: DataTableColumn<Person>[] = [
    { key: 'name', header: 'Name', sortable: true, bold: true },
    { key: 'relationship', header: 'Relationship', sortable: true },
    { key: 'address', header: 'Address' },
    { key: 'cityStateZip', header: 'City, State Zip' },
  ];

  get allPeople(): Person[] {
    return this.caseService.casePersons();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized && changes['preSelectedPeople']) {
      // We now have full Person objects up front, so seed the Map directly —
      // no gap for people who aren't on the first loaded page.
      for (const person of this.preSelectedPeople) {
        this.selectedPeople.set(this.getSelectionKey(person), person);
      }
      this.initialized = true;
    }

    if (changes['caseId'] && this.caseId) {
      this.caseService.getCasePerson(this.caseId, 1, 10);
    }
  }

  ngAfterViewInit(): void {
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    this.setupFocusTrap();
    this.setInitialFocus();
    this.setupDataTableKeyboardNavigation();
  }

  ngOnDestroy(): void {
    // FIX: Cancel all pending timeouts to prevent memory leaks
    this.pendingTimeouts.forEach(timeout => clearTimeout(timeout));
    this.pendingTimeouts = [];

    this.removeFocusTrap();
    
    // FIX: Clear DOM references to allow garbage collection
    this.focusableElements = [];
    this.previouslyFocusedElement = null;
    this.keydownListener = null;
    
    // FIX: Clear selectedPeople Map if it's very large
    this.selectedPeople.clear();
  }

  private setupFocusTrap(): void {
    if (!this.modalBox) return;

    this.focusableElements = Array.from(
      this.modalBox.nativeElement.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), [role="row"]'
      )
    ) as HTMLElement[];

    this.keydownListener = (event: KeyboardEvent) => this.handleKeyDown(event);
    this.modalBox.nativeElement.addEventListener('keydown', this.keydownListener);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (!this.focusableElements || this.focusableElements.length === 0) return;

    const firstFocusable = this.focusableElements[0];
    const lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancel();
      return;
    }

    if (event.key === 'Enter' && activeElement.getAttribute('role') === 'row') {
      event.preventDefault();
      const personName = activeElement.getAttribute('data-person-name');
      if (personName) {
        const person = this.allPeople.find(p => this.getSelectionKey(p) === personName);
        if (person) {
          this.toggleSelect(person);
        }
      }
      return;
    }

    if (event.key === 'Tab') {
      if (event.shiftKey) {
        if (activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    }
  }

  private removeFocusTrap(): void {
    if (this.modalBox && this.keydownListener) {
      this.modalBox.nativeElement.removeEventListener('keydown', this.keydownListener);
      // FIX: Set keydownListener to null to release the reference
      this.keydownListener = null;
    }
  }

  private setInitialFocus(): void {
    if (this.focusableElements && this.focusableElements.length > 0) {
      // FIX: Track timeout to allow cleanup on destroy
      const timeoutId = setTimeout(() => {
        // FIX: Check if component is still alive before focusing
        if (this.focusableElements && this.focusableElements.length > 0) {
          this.focusableElements[0].focus();
        }
      }, 0);
      
      this.pendingTimeouts.push(timeoutId);
    }
  }

  private restoreFocus(): void {
    if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
      // FIX: Track timeout and check if component is alive
      const timeoutId = setTimeout(() => {
        if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
          this.previouslyFocusedElement?.focus();
        }
      }, 0);
      
      this.pendingTimeouts.push(timeoutId);
    }
  }

  private setupDataTableKeyboardNavigation(): void {
    const tableRows = this.modalBox?.nativeElement.querySelectorAll('[role="row"]');
    if (tableRows) {
      tableRows.forEach((row: Element) => {
        (row as HTMLElement).setAttribute('tabindex', '0');
        (row as HTMLElement).setAttribute('aria-label', `Person row - Press Enter to toggle selection`);
      });
    }
  }

  onPageOrSizeChange(event: PageChangeEvent): void {
    this.caseService.changePersonPage(this.caseId, event.page, event.pageSize);
  }

  getSelectionKey = (person: Person): string => {
    return person.name;
  };

  toggleSelect(person: Person): void {
    const key = this.getSelectionKey(person);

    if (this.selectedPeople.has(key)) {
      this.selectedPeople.delete(key);
    } else {
      this.selectedPeople.set(key, person);
    }
  }

  isSelected = (person: Person): boolean => {
    return this.selectedPeople.has(this.getSelectionKey(person));
  };

  save(): void {
    const selectedPeople = Array.from(this.selectedPeople.values());

    this.restoreFocus();
    this.closed.emit({ selectedPeople });
  }

  cancel(): void {
    this.restoreFocus();
    this.closed.emit(null);
  }
}